import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiClock, FiCoffee, FiArrowRight, FiCheckCircle } from 'react-icons/fi';
import Sidebar from '../../Sidebar';
import { getAccountScopedLocalStorageKey } from '../../../utils/utils';
import '../English.css';

const getScopedLocalStorageItem = (key) => {
  const scopedKey = getAccountScopedLocalStorageKey(key);
  return scopedKey ? localStorage.getItem(scopedKey) : null;
};

const setScopedLocalStorageItem = (key, value) => {
  const scopedKey = getAccountScopedLocalStorageKey(key);
  if (!scopedKey) return false;
  localStorage.setItem(scopedKey, value);
  return true;
};

export default function MockExamBreak() {
  const navigate = useNavigate();
  const location = useLocation();
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes in seconds
  const [mockExamInfo, setMockExamInfo] = useState(null);

  useEffect(() => {
    // Get mock exam info from localStorage or location state
    const storedMockExam = JSON.parse(getScopedLocalStorageItem('mockExam') || '{}');
    const locationMockExam = location.state?.mockExamInfo;
    
    if (locationMockExam) {
      setMockExamInfo(locationMockExam);
    } else if (storedMockExam.id) {
      setMockExamInfo(storedMockExam);
    } else {
      // No mock exam info, redirect back
      navigate('/exams/english');
      return;
    }

    // Start countdown timer
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          // Time's up, proceed to next task
          proceedToNextTask();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const proceedToNextTask = () => {
    if (!mockExamInfo) return;

    const nextTaskIndex = mockExamInfo.currentTaskIndex + 1;
    
    if (nextTaskIndex >= mockExamInfo.tasks.length) {
      // All tasks completed, show results
      navigate('/exams/english/mock-results');
      return;
    }

    const nextTask = mockExamInfo.tasks[nextTaskIndex];
    const updatedMockExamInfo = {
      ...mockExamInfo,
      currentTaskIndex: nextTaskIndex,
      isBreak: false
    };

    setScopedLocalStorageItem('mockExam', JSON.stringify(updatedMockExamInfo));

    // Navigate to next task
    const taskRoutes = {
      'Listening': '/exams/english/listening',
      'Reading': '/exams/english/reading',
      'Writing': '/exams/english/writing',
      'Speaking': '/exams/english/speaking'
    };

    navigate(taskRoutes[nextTask.skill], {
      state: { isMockExam: true, mockExamInfo: updatedMockExamInfo }
    });
  };

  const skipBreak = () => {
    proceedToNextTask();
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getNextTaskInfo = () => {
    if (!mockExamInfo || mockExamInfo.currentTaskIndex + 1 >= mockExamInfo.tasks.length) {
      return null;
    }
    return mockExamInfo.tasks[mockExamInfo.currentTaskIndex + 1];
  };

  const getCompletedTasks = () => {
    if (!mockExamInfo) return [];
    return mockExamInfo.tasks.slice(0, mockExamInfo.currentTaskIndex + 1);
  };

  const nextTask = getNextTaskInfo();
  // eslint-disable-next-line no-unused-vars
  const completedTasks = getCompletedTasks();

  if (!mockExamInfo) {
    return <div>Loading...</div>;
  }

  return (
    <div className="english-layout">
      <Sidebar />
      <main className="english-main">
        <div className="mock-break-container">
          <div className="mock-break-header">
            <div className="break-icon">
              <FiCoffee />
            </div>
            <h1>Break Time</h1>
            <p>Take a 10-minute break before your next task</p>
          </div>

          <div className="break-timer">
            <div className="timer-circle">
              <FiClock />
              <span className="timer-text">{formatTime(timeLeft)}</span>
            </div>
          </div>

          <div className="break-progress">
            <h3>Your Progress</h3>
            <div className="progress-tasks">
              {mockExamInfo.tasks.map((task, index) => (
                <div 
                  key={index} 
                  className={`progress-task ${index <= mockExamInfo.currentTaskIndex ? 'completed' : 'upcoming'}`}
                >
                  <div className="task-icon">
                    {index <= mockExamInfo.currentTaskIndex ? (
                      <FiCheckCircle />
                    ) : (
                      <span className="task-number">{index + 1}</span>
                    )}
                  </div>
                  <div className="task-info">
                    <span className="task-name">{task.skill}</span>
                    <span className="task-duration">{task.duration}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {nextTask && (
            <div className="next-task-info">
              <h3>Next Task</h3>
              <div className="next-task-card">
                <h4>{nextTask.skill}</h4>
                <p>{nextTask.description}</p>
                <span className="duration">Duration: {nextTask.duration}</span>
              </div>
            </div>
          )}

          <div className="break-actions">
            <button 
              className="btn-lwz-outline"
              onClick={() => navigate('/exams/english')}
            >
              Exit Mock Exam
            </button>
            <button 
              className="btn-lwz-primary"
              onClick={skipBreak}
            >
              Skip Break <FiArrowRight />
            </button>
          </div>

          <div className="break-tips">
            <h4>Break Tips:</h4>
            <ul>
              <li>Stay hydrated - drink some water</li>
              <li>Take a few deep breaths to relax</li>
              <li>Stretch your arms and shoulders</li>
              <li>Review your strategy for the next task</li>
              <li>Stay focused and confident</li>
            </ul>
          </div>
        </div>
      </main>
    </div>
  );
} 